import { getColorShade } from "../../../Helpers";
import UpdateRanking from "./UpdateRanking";
import { CategoryFull } from "../../../Models";
import {
  XMarkIcon,
  MapPinIcon,
  ArrowTopRightOnSquareIcon,
  ArrowRightIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";
import { CustomTooltip } from "../../Shared/CustomToolTip";
import { deallocateSubtag, deallocateTag } from "../../../api/tag.api";
import useApiToken from "../../../hooks/useApiToken";
import { useState } from "react";
import EditCompanyModal from "./EditCompanyModal";
import EditUniversityModal from "./EditUniversityModal";

interface Props {
  setSelectedRow: (entry: string | null) => void;
  selectedRow : string | null; 
  columnData: any;
  tab: string;
  checked: boolean;
  handleSelectColumn: (columnId: string) => void;
  handleEditRankLabel: (
    category: CategoryFull,
    selectedColumn?: string
  ) => void;
  rankCategoryLabels: CategoryFull[];
  getData: () => Promise<any>;
}

const RankTableColumn = ({
  setSelectedRow,
  selectedRow,
  columnData,
  tab,
  checked,
  handleSelectColumn,
  handleEditRankLabel,
  rankCategoryLabels,
  getData
}: Props) => {
  const token = useApiToken();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleRemoveTag = async (tagId:string, entityId:string, isSubtag: boolean) => {
    if (!token) return;
    try{
      const deallocate = isSubtag ? deallocateSubtag : deallocateTag;
      await deallocate(entityId, tagId, tab === "Companies", "rankboard", token);
      getData();
    }
    catch(ex){
      console.log(ex);
    }
  }

  return (
    <>
      <tr
        className="bg-white border-b dark:bg-gray-900 border-2 cursor-pointer	border-gray-100 dark:border-gray-700"
        key={columnData.id}
        onClick={() => selectedRow === columnData.id ? setSelectedRow(null) : setSelectedRow(columnData.id)}
      >
        <td className="sm:table-cell text-center px-2 w-10">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-gray-100"
            checked={checked}
            onChange={() => handleSelectColumn(columnData.id)}
            onClick = {(event) => event.stopPropagation()}
          />
        </td>

        <td className="px-4 py-1 text-gray-900 dark:text-white font-semibold">
            {
              columnData.name
            }
        </td>
        {tab !== "Companies" && (
          <td className="px-6 py-1 text-gray-900  dark:text-white">
            {columnData?.state?.name}
          </td>
        )}
        {tab !== "Companies" && (
          <td className="px-6 py-1 text-gray-900  dark:text-white">
            <div className="flex gap-2">
              {
                columnData.url &&
                <CustomTooltip content="Visit website">
                  <button
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(columnData.url, '_blank');
                  }}
                  className="border-2 border-gray-300 p-2 rounded"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3 w-3"/>
                  </button>
                </CustomTooltip>
              }
              {
                columnData.linkedIn &&
                <CustomTooltip content="View on linkedin">
                  <button
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(columnData.linkedIn, '_blank');
                  }}
                  className="border-2 border-gray-400 p-2 rounded"
                  >
                    <svg fill="currentColor" viewBox="0 0 24 24" className="h-3 w-3 text-entntblue hover:scale-105 hover:text-gray-400" aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M6.94 5a2 2 0 1 1-4-.002 2 2 0 0 1 4 .002zM7 8.48H3V21h4V8.48zm6.32 0H9.34V21h3.94v-6.57c0-3.66 4.77-4 4.77 0V21H22v-7.93c0-6.17-7.06-5.94-8.72-2.91l.04-1.68z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </CustomTooltip>
              }
            </div>
          </td>
        )}
        <td className="px-6 py-1 flex gap-2 text-gray-600">
          <div
            style={{
              background: columnData.rankingCategory
                ? tab === "Universities"
                  ? `linear-gradient(to right, ${
                      (
                        getColorShade(
                          columnData.rankingCategory.rankNumber,
                          true
                        ) as { grad: string; postGrad: string }
                      ).grad
                    } 50%, ${
                      (
                        getColorShade(
                          columnData.rankingCategory.rankNumber,
                          true
                        ) as { grad: string; postGrad: string }
                      ).postGrad
                    } 50%)`
                  : (getColorShade(
                      columnData.rankingCategory.rankNumber,
                      false
                    ) as string)
                : "gray",
            }}
            className={`flex items-center space-x-2 max-w-max px-2 py-1 rounded-lg font-semibold ${
              columnData.rankingCategory === null
                ? "text-white"
                : columnData.rankingCategory.rankNumber >= 3
                  ? "text-gray-800"
                  : "text-gray-100"
            }
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <UpdateRanking
              selected={columnData.rankingCategory?.rankCategoryId}
              handleEditRankLabel={handleEditRankLabel}
              selectedColumn={columnData.id}
              rankCategoryLabels={rankCategoryLabels}
            />

            <span className="pr-4">
              {columnData.rankingCategory === null
                ? "unranked"
                : columnData.rankingCategory.title}
            </span>
          </div>
        </td>

        {
          tab === "Companies" && (
            <td className="px-2 py-1 text-gray-900 text-center dark:text-white">
              <div className="flex gap-2 text-center">
                 {
                  columnData.url && 
                  <CustomTooltip content="Visit website">
                    <button
                    onClick={(event) => {
                      event.stopPropagation(); 
                      window.open(columnData.url, '_blank');
                    }}
                    className="border-2 border-gray-300 p-2 rounded"
                    >
                      <ArrowTopRightOnSquareIcon className="h-3 w-3"/>
                    </button>
                  </CustomTooltip>
                 }
                 {
                  columnData.linkedIn && 
                  <CustomTooltip content="View on linkedin">
                    <button
                    onClick={(event) => {
                      event.stopPropagation(); 
                      window.open(columnData.linkedIn, '_blank');
                    }}
                    className="border-2 border-gray-400 p-2 rounded"
                    >
                      <svg fill="currentColor" viewBox="0 0 24 24" className="h-3 w-3 text-entntblue hover:scale-105 hover:text-gray-400" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M6.94 5a2 2 0 1 1-4-.002 2 2 0 0 1 4 .002zM7 8.48H3V21h4V8.48zm6.32 0H9.34V21h3.94v-6.57c0-3.66 4.77-4 4.77 0V21H22v-7.93c0-6.17-7.06-5.94-8.72-2.91l.04-1.68z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </CustomTooltip>
                 }
              </div>
            </td>
          )
        }

        <td className="px-2 py-1 text-gray-900  dark:text-white max-w-[300px]">
          <div className="flex flex-wrap gap-3 ">
            {
              columnData.tags && columnData.tags.length > 0 && columnData.tags.map((tag:any, index: number) => 
              <span 
              className="rounded-xl border-2 bg-blue-100 text-entntblue px-2 flex gap-3 items-center dark:bg-entntblue dark:text-blue-100 font-medium"
              key={tag.Id + index}
              >
                {tag.name} 
                <XMarkIcon
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoveTag(tag.id, columnData.id, false);
                }}
                className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:scale-110 hover:text-red-300"
                /> 
              </span>)
            }
            
            {
              columnData.subtags && columnData.subtags.map((subtag:any, index: number) => 
              <span 
              className="rounded-xl border-2 bg-blue-100 text-entntblue px-2 flex gap-3 items-center dark:bg-entntblue dark:text-blue-100 font-medium"
              key={subtag.Id + index}
              >
                {subtag.tagName}
                <ArrowRightIcon className="h-3 w-3"/>
                {subtag.name} 
                <XMarkIcon
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoveTag(subtag.tagSubtagId, columnData.id, true);
                }}
                className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:scale-110 hover:text-red-300"
                /> 
              </span>)
            }


          </div>
        </td>
        
      </tr>
      {
        selectedRow === columnData.id &&
        <tr>
          <td colSpan={tab === "Companies" ? 6 : 7} className="py-4 text-xs">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                {tab === "Companies" ? (
                  <div className="grid grid-cols-2 gap-x-12 gap-y-1 px-16">
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Location:</h4>
                      <p className="text-black dark:text-gray-200 flex gap-1 items-center"> <MapPinIcon className="h-3 w-3"/> {columnData?.locations && columnData?.locations.map((l: any) => l.name).join(", ")}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Revenue:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.revenue || "N/A"}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Stock Market:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.stockMarket || "N/A"}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Relevance:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.relevance || "N/A"}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Hot Startup:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.hotStartup || "N/A"}</p>
                    </div>
                    {/* <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">LinkedIn:</h4>
                      <p className="text-black dark:text-gray-200">
                        {columnData?.linkedIn ? (
                          <a href={columnData.linkedIn} target="_blank" rel="noreferrer" className="text-entntblue hover:underline truncate max-w-[200px] inline-block align-bottom">{columnData.linkedIn}</a>
                        ) : "N/A"}
                      </p>
                    </div> */}
                    {/* <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Website:</h4>
                      <p className="text-black dark:text-gray-200">
                        {columnData?.url ? (
                          <a href={columnData.url} target="_blank" rel="noreferrer" className="text-entntblue hover:underline truncate max-w-[200px] inline-block align-bottom">{columnData.url}</a>
                        ) : "N/A"}
                      </p>
                    </div> */}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-x-12 gap-y-1 px-16">
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">State:</h4>
                      <p className="text-black dark:text-gray-200 flex gap-1 items-center"> <MapPinIcon className="h-3 w-3"/> {columnData?.state?.name || "N/A"}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">City:</h4>
                      <p className="text-black dark:text-gray-200 flex gap-1 items-center"> <MapPinIcon className="h-3 w-3"/> {columnData?.cityName || "N/A"}</p>
                    </div>
                    {/* <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Location:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.location || "N/A"}</p>
                    </div> */}
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Abbreviation:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.abbreviation || "N/A"}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Alternate Names:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.alternateNames || "N/A"}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Package:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.package || "N/A"}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Degrees:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.degrees || "N/A"}</p>
                    </div>
                    <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Recruiters:</h4>
                      <p className="text-black dark:text-gray-200">{columnData?.recruiters || "N/A"}</p>
                    </div>
                    {/* <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">Website:</h4>
                      <p className="text-black dark:text-gray-200">
                        {columnData?.url ? (
                          <a href={columnData.url} target="_blank" rel="noreferrer" className="text-entntblue hover:underline truncate max-w-[200px] inline-block align-bottom">{columnData.url}</a>
                        ) : "N/A"}
                      </p>
                    </div> */}
                    {/* <div className="flex gap-2">
                      <h4 className="font-medium text-gray-500 dark:text-gray-400">LinkedIn:</h4>
                      <p className="text-black dark:text-gray-200">
                        {columnData?.linkedIn ? (
                          <a href={columnData.linkedIn} target="_blank" rel="noreferrer" className="text-entntblue hover:underline truncate max-w-[200px] inline-block align-bottom">{columnData.linkedIn}</a>
                        ) : "N/A"}
                      </p>
                    </div> */}
                  </div>
                )}
              </div>
              <div className="pr-8">
                <CustomTooltip content="Edit">
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsEditModalOpen(true);
                    }}
                    className="border-2 border-gray-300 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <PencilSquareIcon className="h-4 w-4 text-gray-600 dark:text-gray-300"/>
                  </button>
                </CustomTooltip>
              </div>
            </div>
          </td>
        </tr>
      }
      {tab === "Companies" ? (
        <EditCompanyModal
          isOpen={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          onSave={() => getData()}
          companyData={columnData}
        />
      ) : (
        <EditUniversityModal
          isOpen={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          onSave={() => getData()}
          universityData={columnData}
        />
      )}
    </>
  );
};

export default RankTableColumn;
